import { Encounter, QuestionnaireResponseItem } from "@medplum/fhirtypes";

import { EncounterServiceDateDetailsFieldsType } from "@/encounters/zod-schemas/encounter-service-date-details";
import { combineDateTimeInTimezone, getLocalDateAndTimeInTimezone } from "@/utils/datetime";
import { withTimezoneExtension } from "@/utils/fhir-timezone";

import { getEncounterServiceDateTimezone } from "./encounter-timezone";
import { toServiceDateTimezoneFormValue } from "./service-date-timezone-form";

/**
 * Builds amendment form service-date fields from an encounter resource.
 *
 * @param encounter Encounter whose period and timezone extension are read.
 * @returns Wall-clock date, time, and timezone coding for amendment forms.
 */
export function getEncounterServiceDateDetailsFromEncounter(
  encounter: Encounter,
): EncounterServiceDateDetailsFieldsType {
  const encounterStartDateTimezone = toServiceDateTimezoneFormValue(
    getEncounterServiceDateTimezone(encounter),
  );
  const periodStart = encounter.period?.start;

  if (!periodStart) {
    const now = new Date();
    return {
      encounterStartDate: now,
      encounterStartTime: now.toTimeString().slice(0, 5),
      encounterStartDateTimezone,
    };
  }

  const { date, time } = getLocalDateAndTimeInTimezone(
    periodStart,
    encounterStartDateTimezone.code,
  );

  return {
    encounterStartDate: date,
    encounterStartTime: time,
    encounterStartDateTimezone,
  };
}

/**
 * Applies amendment service-date fields onto an encounter copy.
 *
 * @param encounter Encounter to update.
 * @param details Service-date form values from amendment encounter details.
 * @returns Encounter with period.start and timezone extension updated when values are present.
 */
export function applyEncounterServiceDateDetailsToEncounter(
  encounter: Encounter,
  details: Partial<EncounterServiceDateDetailsFieldsType> | undefined,
): Encounter {
  if (!details?.encounterStartDate || !details.encounterStartTime) {
    return encounter;
  }

  const timezone = details.encounterStartDateTimezone;
  if (!timezone?.code) {
    return encounter;
  }

  const periodStart = combineDateTimeInTimezone(
    details.encounterStartDate,
    details.encounterStartTime,
    timezone.code,
  );

  if (!periodStart) {
    return encounter;
  }

  return withTimezoneExtension(
    {
      ...encounter,
      period: {
        ...encounter.period,
        start: periodStart,
      },
    },
    timezone,
  );
}

/**
 * Builds questionnaire response items for amendment service-date fields.
 *
 * @param details Service-date form values from encounter amendment details.
 * @returns Questionnaire response items for start date and timezone answers.
 */
export function getEncounterServiceDateDetailQuestionnaireItems(
  details?: Partial<EncounterServiceDateDetailsFieldsType>,
): QuestionnaireResponseItem[] {
  const items: QuestionnaireResponseItem[] = [];
  const periodStart = combineDateTimeInTimezone(
    details?.encounterStartDate ?? null,
    details?.encounterStartTime ?? null,
    details?.encounterStartDateTimezone?.code,
  );

  if (periodStart) {
    items.push({
      linkId: "encounterStartDate",
      text: "Start Date",
      answer: [{ valueDateTime: periodStart }],
    });
  }

  if (details?.encounterStartDateTimezone?.code) {
    items.push({
      linkId: "encounterStartDateTimezone",
      text: "Start Date Timezone",
      answer: [{ valueCoding: details.encounterStartDateTimezone }],
    });
  }

  return items;
}
